import React from 'react';
import { Link } from 'react-router-dom';
import { useUserData } from '../context/UserDataContext';
import { useAuth } from '../context/AuthContext';
import './UserJourney.css';

const formatCurrency = (value) => {
  if (value >= 10000000) {
    return '\u20B9' + (value / 10000000).toFixed(1) + ' Cr';
  }
  if (value >= 100000) {
    return '\u20B9' + (value / 100000).toFixed(1) + ' L';
  }
  return '\u20B9' + value.toLocaleString('en-IN');
};

const steps = [
  {
    icon: '\uD83C\uDFAF',
    title: 'Know Your Number',
    description: 'Find your Freedom Number \u2014 the corpus where work becomes optional.',
    time: '2 minutes \u00B7 No signup needed',
    cta: 'Start Here \u2192',
    route: '/tools/quick-check',
    ctaStyle: 'primary',
  },
  {
    icon: '\uD83D\uDCCB',
    title: 'Build Your Complete Plan',
    description: 'Add life goals, review insurance, check loans \u2014 get your total plan.',
    time: '10 minutes \u00B7 Save your progress',
    cta: 'Build Your Plan \u2192',
    route: '/financial-health-check',
    ctaStyle: 'outline',
  },
  {
    icon: '\uD83D\uDD2C',
    title: 'Stress Test It',
    description: '1,000 simulations. 45 years of market data. See if your plan survives.',
    time: '3 minutes \u00B7 Know your odds',
    cta: 'Test Your Plan \u2192',
    route: '/tools/stress-test',
    ctaStyle: 'outline',
  },
  {
    icon: '\uD83D\uDCCA',
    title: 'Track & Grow',
    description: 'Add your investments. See live returns. Watch your freedom get closer.',
    time: 'Ongoing \u00B7 Your personal dashboard',
    cta: 'Start Tracking \u2192',
    route: '/tools/portfolio',
    ctaStyle: 'outline',
  },
];

const explorationTools = [
  {
    icon: '\uD83C\uDFD9\uFE0F',
    title: 'City Finder',
    description:
      'Compare retirement costs across 62 Indian cities. Your money goes further in the right place.',
    cta: 'Compare Cities \u2192',
    route: '/city-finder',
  },
  {
    icon: '\uD83D\uDD04',
    title: 'Plan B \u2014 Relocation',
    description:
      "What if your corpus isn't enough? See how relocating to a lower-cost city changes everything.",
    cta: 'See Plan B \u2192',
    route: '/tools/relocation',
  },
  {
    icon: '\uD83C\uDFE0',
    title: 'Reverse Mortgage',
    description:
      'Own a home? See how much monthly income it could generate \u2014 without selling it.',
    cta: 'Calculate \u2192',
    route: '/tools/reverse-mortgage',
  },
  {
    icon: '\uD83D\uDCA1',
    title: 'Where to Invest Now',
    description:
      'Get a personalised allocation strategy based on your age, risk profile, and freedom timeline.',
    cta: 'Get Strategy \u2192',
    route: '/tools/optimized-strategy',
  },
];

const UserJourney = () => {
  const { userData } = useUserData();
  const { user } = useAuth();

  const getStepCompletion = (index) => {
    if (!user) return { completed: false, value: null };

    switch (index) {
      case 0:
        if (userData.calculatedFreedomNumber > 0) {
          return { completed: true, value: formatCurrency(userData.calculatedFreedomNumber) };
        }
        return { completed: false, value: null };
      case 1: {
        const goals = userData.goals || {};
        const hasModifiedGoal = Object.values(goals).some(
          (g) => (typeof g === 'object' && g.modified) || (typeof g === 'number' && g > 0)
        );
        if (hasModifiedGoal) {
          return { completed: true, value: 'Plan saved' };
        }
        return { completed: false, value: null };
      }
      case 2:
        if (userData.stressTestCompleted) {
          const rate = userData.stressTestResults?.successRate;
          return { completed: true, value: rate != null ? `${rate}% success` : 'Completed' };
        }
        return { completed: false, value: null };
      case 3:
        if (userData.portfolioTotal > 0) {
          return { completed: true, value: formatCurrency(userData.portfolioTotal) };
        }
        return { completed: false, value: null };
      default:
        return { completed: false, value: null };
    }
  };

  const completions = steps.map((_, i) => getStepCompletion(i));
  const nextStepIndex = completions.findIndex((c) => !c.completed);

  return (
    <section className="journey-section">
      <div className="journey-container">
        <div className="journey-header">
          <h2 className="journey-title">Your Journey to Financial Freedom</h2>
          <p className="journey-subtitle">Just 4 steps. Completely free.</p>
        </div>

        <div className="journey-steps">
          {steps.map((step, index) => {
            const { completed, value } = completions[index];
            const isNext = index === nextStepIndex;

            const cardClass = [
              'journey-step',
              completed ? 'completed' : '',
              isNext ? 'next-step' : '',
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <div key={index} className={cardClass}>
                <div className={`journey-step-number ${completed ? 'complete' : 'incomplete'}`}>
                  {completed ? '\u2713' : index + 1}
                </div>
                <span className="journey-step-icon">{step.icon}</span>
                <h3 className="journey-step-title">{step.title}</h3>
                <p className="journey-step-desc">{step.description}</p>
                <span className="journey-step-time">{step.time}</span>
                {completed && value && <span className="journey-step-value">{value}</span>}
                {isNext && !completed && <span className="journey-next-label">Next</span>}
                <Link
                  to={step.route}
                  className={`journey-cta ${completed ? 'outline' : step.ctaStyle}`}
                >
                  {step.cta}
                </Link>
              </div>
            );
          })}
        </div>

        {/* Sharpen Your Plan - Exploration Tools */}
        <div className="journey-divider" />
        <div className="journey-explore-header">
          <h3 className="journey-explore-title">Done with the basics? Sharpen your plan.</h3>
          <p className="journey-explore-subtitle">
            These tools help you explore smarter options &mdash; a cheaper city, a backup plan, or a
            better investment strategy.
          </p>
        </div>
        <div className="journey-explore-grid">
          {explorationTools.map((tool, index) => (
            <Link key={index} to={tool.route} className="journey-explore-card">
              <span className="journey-explore-icon">{tool.icon}</span>
              <h4 className="journey-explore-card-title">{tool.title}</h4>
              <p className="journey-explore-card-desc">{tool.description}</p>
              <span className="journey-explore-cta">{tool.cta}</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default UserJourney;
